// PermitPortal — public student-facing page for entry-permission links.
// Reached via /permit/:token (sent by SMS/email by the office).
// Student can: view the ticket, grant permission + propose windows,
// see office-proposed windows and one-tap Accept / Counter-propose,
// and reply through a lightweight message thread. Two-way scheduling.
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, PaperPlaneTilt, Shield, Warning, Buildings } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PermitPortal() {
  const { token } = useParams();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [signature, setSignature] = useState("");
  const [contact, setContact] = useState("");
  const [windows, setWindows] = useState([{ start: "", end: "", label: "Weekday morning" }]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [counter, setCounter] = useState({ start: "", end: "", label: "" });
  const [showCounter, setShowCounter] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/api/public/chew/permit/${token}`);
      setT(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "This link is invalid or has expired.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function grant() {
    if (!signature.trim()) { alert("Please type your initials to authorize entry."); return; }
    setBusy(true);
    try {
      const clean = windows
        .filter(w => w.start.trim() && w.end.trim())
        .map(w => ({ start: w.start, end: w.end, label: w.label || "" }));
      await axios.post(`${BASE}/api/public/chew/permit/${token}/grant`,
        { signature, contact, entry_windows: clean });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  }

  async function acceptAdminWindow(idx) {
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/chew/permit/${token}/respond`,
        { window_index: idx, message: "" });
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  async function counterPropose() {
    if (!counter.start.trim() || !counter.end.trim()) { alert("Add both start and end time"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/chew/permit/${token}/respond`, {
        counter_start: counter.start, counter_end: counter.end,
        counter_label: counter.label, message: "",
      });
      setCounter({ start: "", end: "", label: "" });
      setShowCounter(false);
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  async function sendMessage() {
    if (!message.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/chew/permit/${token}/respond`, { message });
      setMessage("");
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="permit-error">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-sm">
          <Warning size={48} className="text-red-500 mx-auto mb-3"/>
          <div className="font-black text-lg mb-1">Link not available</div>
          <div className="text-sm text-slate-500">{error}</div>
          <div className="text-xs text-slate-400 mt-4">If you think this is a mistake, reply to the message we sent and we'll re-send a fresh link.</div>
        </div>
      </div>
    );
  }

  if (!t) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="permit-loading">Loading…</div>;

  const acceptedWindow = t.accepted_window;
  const adminWindows = t.admin_proposed_windows || [];
  const tenantWindows = t.tenant_proposed_windows || [];
  const granted = t.already_granted;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4" data-testid="permit-portal">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-cyan-600 bg-cyan-50 rounded-full px-3 py-1">
            <Buildings size={12}/> Chew Flats · Maintenance
          </div>
          <h1 className="text-2xl font-black mt-3">Entry permission</h1>
          <p className="text-xs text-slate-500 mt-1">Unit {t.unit} · {t.address}</p>
        </div>

        {/* The ticket */}
        <div className="bg-white rounded-2xl p-5 shadow-sm mb-4" data-testid="permit-ticket">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Work item</div>
          <div className="font-black text-lg text-slate-900">{t.subject}</div>
          {t.description && <div className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{t.description}</div>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
              t.priority === "urgent" ? "bg-red-100 text-red-700"
              : t.priority === "high" ? "bg-orange-100 text-orange-700"
              : "bg-cyan-50 text-cyan-700"
            }`}>{t.priority}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full capitalize bg-slate-100 text-slate-600`}>{t.status?.replace(/_/g," ")}</span>
          </div>
        </div>

        {/* Accepted window banner */}
        {acceptedWindow && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-4" data-testid="permit-accepted">
            <div className="flex items-center gap-2 text-emerald-800 font-black">
              <CheckCircle weight="fill" size={20}/> Visit is scheduled
            </div>
            <div className="mt-2 font-bold text-slate-900">{acceptedWindow.label || "Maintenance visit"}</div>
            <div className="text-sm text-emerald-800">{acceptedWindow.start} → {acceptedWindow.end}</div>
            {acceptedWindow.note && <div className="text-xs italic text-emerald-700 mt-1">{acceptedWindow.note}</div>}
            <div className="text-xs text-emerald-700 mt-2">You'll get a reminder before we arrive. Reply below if you need to reschedule.</div>
          </div>
        )}

        {/* Office proposed windows */}
        {!acceptedWindow && adminWindows.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm mb-4" data-testid="permit-admin-windows">
            <div className="text-[10px] uppercase font-black tracking-widest text-cyan-600 mb-2">Office proposed</div>
            <div className="text-sm text-slate-600 mb-3">The maintenance team suggested these times. Tap one to confirm.</div>
            <div className="space-y-2">
              {adminWindows.map((w, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`permit-admin-window-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{w.label || `Window ${i + 1}`}</div>
                    <div className="text-xs text-slate-500">{w.start} → {w.end}</div>
                    {w.note && <div className="text-xs italic text-slate-500 mt-0.5">{w.note}</div>}
                  </div>
                  <button disabled={busy} onClick={() => acceptAdminWindow(i)}
                    className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-black px-3 py-2 rounded-lg"
                    data-testid={`permit-accept-window-${i}`}>Accept</button>
                </div>
              ))}
            </div>
            <button onClick={() => setShowCounter(v => !v)}
              className="mt-3 text-xs font-bold text-cyan-600 hover:text-cyan-700"
              data-testid="permit-counter-toggle">
              {showCounter ? "Cancel counter-propose" : "None of these work — suggest another time"}
            </button>
            {showCounter && (
              <div className="mt-3 space-y-2" data-testid="permit-counter-form">
                <WindowRow value={counter} onChange={setCounter} testIdPrefix="permit-counter"/>
                <button disabled={busy} onClick={counterPropose}
                  className="w-full bg-slate-900 text-white text-sm font-black py-2.5 rounded-lg disabled:opacity-40"
                  data-testid="permit-counter-submit">Send counter-proposal</button>
              </div>
            )}
          </div>
        )}

        {/* Grant permission form (only if not yet granted) */}
        {!granted && !acceptedWindow && (
          <div className="bg-white rounded-2xl p-5 shadow-sm mb-4" data-testid="permit-grant-form">
            <div className="flex items-center gap-2 text-cyan-700 font-black mb-2"><Shield size={18}/> Authorize entry</div>
            <p className="text-xs text-slate-500 mb-4">Type your initials to grant our maintenance team permission to enter unit {t.unit} to complete this work. Add times below that work for you.</p>

            <div className="space-y-3">
              <label className="block">
                <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Your initials</div>
                <input value={signature} onChange={e => setSignature(e.target.value)} placeholder="e.g. JS"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="permit-signature"/>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Callback (optional)</div>
                <input value={contact} onChange={e => setContact(e.target.value)} placeholder="phone or email (private)"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="permit-contact"/>
              </label>

              <div className="pt-2">
                <div className="flex items-center gap-2 text-slate-700 font-black text-sm mb-2"><Clock size={14}/> Preferred entry times</div>
                {windows.map((w, i) => (
                  <div key={i} className="mb-2">
                    <WindowRow value={w} onChange={(nw) => setWindows(prev => prev.map((x, j) => j === i ? nw : x))}
                      testIdPrefix={`permit-win-${i}`}/>
                  </div>
                ))}
                {windows.length < 3 && (
                  <button onClick={() => setWindows(prev => [...prev, { start: "", end: "", label: "" }])}
                    className="text-xs font-bold text-cyan-600 hover:text-cyan-700" data-testid="permit-add-window">
                    + add another window
                  </button>
                )}
              </div>

              <button disabled={busy} onClick={grant}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white font-black py-3 rounded-xl mt-3"
                data-testid="permit-grant-submit">
                {busy ? "Submitting…" : "Grant permission"}
              </button>
              <p className="text-[10px] text-slate-400 text-center mt-2">Timestamp + your device info are recorded as an audit stamp.</p>
            </div>
          </div>
        )}

        {/* Granted confirmation */}
        {granted && !acceptedWindow && (
          <div className="bg-white rounded-2xl p-5 shadow-sm mb-4" data-testid="permit-granted-banner">
            <div className="flex items-center gap-2 text-emerald-800 font-black">
              <CheckCircle weight="fill" size={18}/> Permission on file
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Signed by <strong>{t.granted_details?.signature}</strong>. The office will confirm a time below.
            </div>
            {tenantWindows.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Your suggested times</div>
                <div className="space-y-1">
                  {tenantWindows.map((w, i) => (
                    <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
                      {w.label ? `${w.label} · ` : ""}{w.start} → {w.end}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Message thread */}
        <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="permit-thread">
          <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Messages</div>
          <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
            {(t.messages || []).map((m, i) => (
              <div key={i}
                className={`text-sm rounded-lg p-2.5 ${m.from === "admin" ? "bg-cyan-50 mr-8 text-cyan-900" : "bg-slate-100 ml-8"}`}
                data-testid={`permit-msg-${i}`}>
                <div className="text-[10px] uppercase font-black tracking-wider opacity-60">{m.from === "admin" ? "Maintenance office" : "You"}</div>
                <div>{m.text}</div>
              </div>
            ))}
            {!(t.messages || []).length && <div className="text-xs text-slate-400 italic">No messages yet — send one below.</div>}
          </div>
          <div className="flex gap-2">
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Type a message…"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="permit-msg-input"/>
            <button disabled={busy || !message.trim()} onClick={sendMessage}
              className="bg-slate-900 text-white rounded-lg px-4 disabled:opacity-40" data-testid="permit-msg-send">
              <PaperPlaneTilt weight="fill" size={16}/>
            </button>
          </div>
        </div>

        <div className="text-center text-[10px] text-slate-400 mt-6">
          Property Management Radar · secure entry-permission link
        </div>
      </div>
    </div>
  );
}

function WindowRow({ value, onChange, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-2">
      <input value={value.label || ""} onChange={e => onChange({ ...value, label: e.target.value })}
        placeholder="Label (e.g. Weekday morning)"
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`${testIdPrefix}-label`}/>
      <div className="grid grid-cols-2 gap-2">
        <input value={value.start || ""} onChange={e => onChange({ ...value, start: e.target.value })}
          placeholder="Start (e.g. Tue 9am)"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`${testIdPrefix}-start`}/>
        <input value={value.end || ""} onChange={e => onChange({ ...value, end: e.target.value })}
          placeholder="End (e.g. Tue 12pm)"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`${testIdPrefix}-end`}/>
      </div>
    </div>
  );
}
